import React, { useEffect, useMemo, useState } from 'react';
import { Button, LinkText, Table } from '@adc/parallax-component-library';
import DynamicTable, { CheckboxFilterSection } from '../../table/DynamicTable';
import CreateSubjectModal from '../../modal/CreateSubjectModal';
import { Site, Subject } from '../../../models';
import { EntityStatus } from 'adc-sirius-module-common';
import i18n from '../../../utilities/i18n';
import { enumToCheckboxOptions } from '../../../utilities/helpers';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createSubjectMutateOptions,
  subjectsQueryOptions,
} from '../../../queryOptions';

type SubjectsTableProps = {
  isLoading: boolean;
  hasLoadingError: boolean;
  subjects: Subject[];
  sites: Site[];
  navigateToSite: (siteKey: string, siteLabel: string) => void;
  navigateToSubject: (subjectKey: string, subjectLabel: string) => void;
  canCreateSubject: boolean;
};

export const SubjectsTable = ({
  isLoading,
  hasLoadingError,
  subjects,
  sites,
  navigateToSite,
  navigateToSubject,
  canCreateSubject,
}: SubjectsTableProps) => {
  const queryClient = useQueryClient();

  const [filteredSubjects, setFilteredSubjects] = useState<Subject[]>([]);
  const [subjectStatusFilters, setSubjectStatusFilters] = useState<
    CheckboxFilterSection[]
  >([
    {
      id: 'status',
      label: i18n.t('Entity.status'),
      items: enumToCheckboxOptions(EntityStatus, 'Status'),
    },
  ]);

  const createSubject = useMutation(
    createSubjectMutateOptions({
      onSuccess: (subject) => {
        queryClient.setQueryData(
          subjectsQueryOptions({ siteKey: subject.siteKey }).queryKey,
          (subjects: Subject[] | undefined) => [...(subjects ?? []), subject],
        );
      },
    }),
  );

  useEffect(() => {
    let nextFilteredSubjects = subjects;

    subjectStatusFilters.forEach((filter) => {
      const activeFilterValues = filter.items
        .filter((item) => item.value)
        .map((item) => item.id);

      if (activeFilterValues.length > 0 && filter.id === 'status') {
        nextFilteredSubjects = nextFilteredSubjects.filter((subject) =>
          activeFilterValues.includes(String(subject.status)),
        );
      }
    });

    setFilteredSubjects(nextFilteredSubjects);
  }, [subjectStatusFilters, subjects]);

  const getStatusLabel = (status: EntityStatus) => {
    return i18n.t(`Status.${EntityStatus[status].toLowerCase()}`);
  };

  const siteNameByKey = useMemo(
    () => new Map<string, string>(sites.map((site) => [site.key, site.name])),
    [sites],
  );

  const subjectsTableColumnData = [
    { label: i18n.t('Entity.name'), dataKey: 'name' },
    {
      label: i18n.t('Entity.site'),
      dataKey: 'siteKey',
      getSortValue: (subject: Subject) =>
        siteNameByKey.get(subject.siteKey) || '',
    },
    {
      label: i18n.t('Entity.status'),
      dataKey: 'status',
      getSortValue: (subject: Subject) => getStatusLabel(subject.status),
    },
  ];

  const subjectsTableCellRenderer = (dataKey: string, subject: Subject) => {
    if (dataKey === 'siteKey') {
      const siteName = siteNameByKey.get(subject.siteKey) || '';

      return (
        <Table.TextCell key={dataKey}>
          <LinkText
            onPress={() => navigateToSite(subject.siteKey, siteName)}
            type="$bodySmall.default"
          >
            {siteName}
          </LinkText>
        </Table.TextCell>
      );
    } else if (dataKey === 'name') {
      return (
        <Table.TextCell key={dataKey}>
          <LinkText
            onPress={() => navigateToSubject(subject.key, subject.name)}
            type="$bodySmall.default"
          >
            {subject[dataKey]}
          </LinkText>
        </Table.TextCell>
      );
    } else if (dataKey === 'status') {
      return (
        <Table.TextCell key={dataKey}>
          {getStatusLabel(subject.status)}
        </Table.TextCell>
      );
    } else {
      return <Table.TextCell key={dataKey}>{subject[dataKey]}</Table.TextCell>;
    }
  };

  return (
    <DynamicTable
      isLoading={isLoading}
      hasLoadingError={hasLoadingError}
      title={i18n.t('Page_Study.table_subjects_title')}
      description={i18n.t('Page_Study.table_subjects_description')}
      columnData={subjectsTableColumnData}
      rowData={filteredSubjects}
      cellRenderer={subjectsTableCellRenderer}
      enableFiltering={true}
      checkboxFilters={subjectStatusFilters}
      onFiltersChange={setSubjectStatusFilters}
      onFiltersReset={() => {
        setSubjectStatusFilters((prevFilters) =>
          prevFilters.map((filter) => ({
            ...filter,
            items: filter.items.map((item) => ({
              ...item,
              value: false,
            })),
          })),
        );
      }}
      toolbarRightContent={
        canCreateSubject ? (
          <CreateSubjectModal
            sites={sites}
            trigger={
              <Button
                size="medium"
                label={i18n.t('Page_Study.add_subject')}
                onPress={() => {}}
                disabled={createSubject.isPending}
                testID="create-subject-button"
              />
            }
            onSuccess={async (newSubject: Subject) => {
              await createSubject.mutateAsync({ subject: newSubject });
            }}
          />
        ) : null
      }
      testID="subjects-table"
    />
  );
};
